/**
 * useCollectionsData — composite hook that orchestrates playlist data fetching,
 * tab state, search filtering, and derived values for the collections UI.
 *
 * Wraps useProfilePlaylists, useFollowedPlaylists, and useInProgressEnrichment
 * into a single hook whose return shape aligns with ProfilePlaylistTabsProps.
 *
 * Shared between CommunityProfileUserView (profile page) and
 * a future standalone playlist page.
 */

import {
  useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef,
} from 'react'
import { useQuery } from '@apollo/client'
import _get from 'lodash/get'
import type { Dispatch, UnknownAction } from 'redux'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
  APOLLO_CLIENT_ERROR_POLICY_ALL,
} from '../../../apollo-graphql/index'
import { GET_USER_META_TAGS } from '../../query'
import { getAllowedPlaylistVisibilities } from '../../index'
import useProfilePlaylists from './useProfilePlaylists'
import useFollowedPlaylistsHook from './useFollowedPlaylists'
import useInProgressEnrichment from './useInProgressEnrichment'
import useWatchHistoryData from './useWatchHistoryData'
import { SYSTEM_PLAYLIST_TYPES } from './content-utils'
import useLatest from '../../shared/use-latest'
import useDebouncedValue from '../../shared/useDebouncedValue'
import { TABS, type CollectionsTab } from '../../../../components/Community/Identity/Library/PlaylistTabs/index'
import type {
  ActivityVisibilityValue,
  GetUserMetaTagsResponse,
  InProgressItem,
  Playlist,
  StaticTextMap,
} from '../../types'

export interface UseCollectionsDataParams {
  uid: string
  /**
   * Viewer uuid — threaded to `useProfilePlaylists` for `cache.identify` on
   * `UserProfile` (whose Apollo `keyFields` is `['uuid']`).  Separate from
   * `uid` because `useFollowedPlaylists` uses `uid` as a GraphQL variable.
   */
  uuid?: string
  language: string
  jwt: string
  isLoggedIn: boolean
  dispatch: Dispatch<UnknownAction>
  staticText: StaticTextMap
  history?: { push: (path: string) => void }
  authorUsername: string
  authorUid?: string
  profileVisibility?: string
  inProgress?: InProgressItem[]
  /**
   * `loading` of the upstream `GET_PROFILE_IN_PROGRESS_CAROUSEL` query owned
   * by the caller. Combined here with `useInProgressEnrichment`'s internal
   * `isHydrating` (title-group hydration) to derive `inProgressIsHydrating`
   * — the single signal `ProfileMyPlaylists` uses to render the Currently
   * Watching skeleton vs the empty-state. Defaults to `false` for callers
   * that don't wait on a separate carousel query.
   */
  inProgressLoading?: boolean
  isPreview?: boolean
  /** When false, skips playlist + meta-tag queries (e.g. community feature gate will 404). */
  queriesEnabled?: boolean
  /**
   * Whether the *page-level* on-load queries owned by the caller have settled
   * (e.g. on the library page: the light profile query, the in-progress
   * carousel query, and the purchases query).  Combined here with this hook's
   * own on-load queries (`GET_MY_PLAYLISTS`, `GET_USER_HISTORY`) to decide when
   * it's safe to fire the heavy followed-playlists items query.  Defaults to
   * `true` for callers (e.g. the profile page) that have no extra page-level
   * queries to wait on.
   */
  pageQueriesSettled?: boolean
  /** Profile-level activity visibility flags (CO-488).  Both contribute to the
   *  privacy summary's "Sharing N" count: each that's `'public'` adds 1.
   *  Currently Watching and Watch History are NOT playlists — they're
   *  separate `FieldVisibility` flags on the user profile, so they have to
   *  be plumbed in alongside the playlist data. */
  currentlyWatchingVisibility?: ActivityVisibilityValue | null
  watchHistoryVisibility?: ActivityVisibilityValue | null
  /**
   * Initial active tab — e.g. deep-linked via `?tab=discover` (or
   * `?tab=following`) on the library page. Read once at mount to seed the tab
   * state; defaults to My Playlists when omitted or invalid.
   */
  initialTab?: CollectionsTab
}

export default function useCollectionsData ({
  uid,
  uuid,
  language,
  jwt,
  isLoggedIn,
  dispatch,
  staticText,
  history,
  authorUsername,
  authorUid,
  profileVisibility = 'public',
  inProgress,
  inProgressLoading = false,
  isPreview,
  queriesEnabled = true,
  pageQueriesSettled = true,
  initialTab,
  // NOTE: `currentlyWatchingVisibility` and `watchHistoryVisibility` are
  // intentionally NOT destructured here even though they're on the params
  // interface.  They used to feed `sharedOnProfileCount`, but the privacy
  // summary now counts only custom playlists (see useMemo below).  Callers
  // still pass them for forward-compat — they're silently ignored.
}: UseCollectionsDataParams) {
  // ── Local state: tab & search ──────────────────────────────────────
  // Declared up here (rather than further down) because the followed-
  // playlists hook below needs to know whether the Following tab has been
  // visited so it can fetch its (heavy) items payload eagerly.
  const [collectionsTab, setCollectionsTab] = useState<CollectionsTab>(
    initialTab ?? TABS.MY_PLAYLISTS,
  )
  const [collectionsSearch, setCollectionsSearch] = useState('')

  // Debounced copy threaded to the *server-side* followed-playlists query
  // (Following tab).  My Playlists' in-memory filter (`searchResultIds`
  // below) intentionally keeps the raw `collectionsSearch` so typing stays
  // snappy on the locally-held playlist list — only the network round-trip
  // is debounced.
  const debouncedCollectionsSearch = useDebouncedValue(collectionsSearch, 250)

  // Sticky latch: once the Following tab has ever been active in this session
  // we keep fetching its data so Apollo's cache stays warm and switching
  // back doesn't trigger a fresh network round-trip. Before that first
  // activation, GET_FOLLOWED_PLAYLISTS_SHELL is skipped entirely so the
  // default My Playlists landing doesn't pay for data the user can't see.
  //
  // PR 13 — Reading B stagger.  The latch now fires from three paths:
  //   (a) initial mount on /library?tab=following → useState initialiser
  //       returns true → Following shell fires immediately.
  //   (b) tab click before the background stagger → render-phase
  //       setFollowingEverActive flips the latch synchronously so the
  //       click feels instant (no second-mount delay).
  //   (c) background stagger → after MyPlaylists shell + watch history
  //       have BOTH settled, a useEffect flips the latch so the
  //       Following shell pre-fetches quietly.  First click on Following
  //       is then a cache hit.
  // A useRef guards both (b) and (c) — once either has fired, the other
  // is a no-op (prevents redundant work and avoids a double-fetch
  // scenario where the user clicks Following on the exact tick the
  // stagger effect would also fire).
  const [followingEverActive, setFollowingEverActive] = useState(
    collectionsTab === TABS.FOLLOWING,
  )
  // Initialize ref to the latch's initial value — not `false` — so an
  // initial-mount on Following doesn't trigger a redundant stagger
  // effect on the next commit.
  const followingLatchFiredRef = useRef(followingEverActive)
  // Moved from render-phase setState to useLayoutEffect.
  // The render-phase form (`if (...) setState(...)` directly in the
  // component body) was producing a paired `nested-update` commit on every
  // render of this hook's host, because React schedules an immediate
  // follow-up commit when a setState fires synchronously during render.
  // useLayoutEffect fires synchronously after DOM mutation, BEFORE paint —
  // so tab-click responsiveness is preserved (no extra frame of latency,
  // unlike useEffect) but the setState now happens during the commit phase
  // and folds into a single React update rather than spawning a sibling
  // nested-update commit.
  useLayoutEffect(() => {
    if (collectionsTab === TABS.FOLLOWING && !followingEverActive) {
      followingLatchFiredRef.current = true
      setFollowingEverActive(true)
    }
  }, [collectionsTab, followingEverActive])
  // ── Profile playlists (own playlists, system playlists, mutations) ─
  // Per-row hydration means individual rows own their own item+count+
  // duration via useRowHydration. Variants take the PlaylistShell entity
  // directly.
  const {
    // No longer destructure `playlistsRawData`. Apollo's response wrapper
    // is recreated on every cache broadcast; consumers here read the
    // inner `allRawPlaylists` array instead.
    playlistsLoading,
    refetchPlaylists,
    loadMorePlaylists,
    isLoadingMorePlaylists,
    allRawPlaylists,
    allUserPlaylistsJS,
    defaultPlaylist,
    favoritesPlaylist,
    handleUpdatePlaylistVisibility,
    handlePlaylistAction,
    handleRemovePlaylistItem,
    handleRemoveWatchLaterItem,
    handleRemoveFavoritesItem,
    handleReorderPlaylistItem,
    handleReorderWatchLaterItem,
    handleReorderFavoritesItem,
    handleHideInProgressItem,
  } = useProfilePlaylists({
    uuid,
    language,
    jwt,
    isLoggedIn,
    dispatch,
    staticText,
    history,
    queriesEnabled,
  })

  // ── Watch History ──────────────────────────────────────────────────
  // Fetched before the followed-playlists hook so `watchHistoryLoading` can
  // feed the readiness gate below.
  const {
    watchHistoryItems,
    watchHistoryTotalCount,
    watchHistoryTotalDuration,
    watchHistoryLoading,
    onRemoveWatchHistoryItem,
  } = useWatchHistoryData({ language, jwt, isLoggedIn })

  // PR 13 — Reading B stagger.  Once page-level queries (caller-owned),
  // MyPlaylists shell, and watch history have ALL settled, flip the
  // Following latch so its shell pre-fetches in the background.  First
  // click on Following is then a cache hit.  The ref guard ensures this
  // runs at most once per mount — and short-circuits the work if the
  // tab-click path (above) or the initial-mount path (useState initialiser)
  // already fired the latch.
  //
  // No setTimeout / artificial delay: Apollo's `loading` flag flips on
  // the same tick the query resolves, React schedules the effect for
  // the next commit — that natural microtask boundary IS the stagger.
  useEffect(() => {
    if (followingLatchFiredRef.current) return
    if (
      pageQueriesSettled
      && !playlistsLoading
      && !watchHistoryLoading
    ) {
      followingLatchFiredRef.current = true
      setFollowingEverActive(true)
    }
  }, [pageQueriesSettled, playlistsLoading, watchHistoryLoading])

  // ── Followed playlists (Following tab) ─────────────────────────────
  // Tab-scoped deferral: GET_FOLLOWED_PLAYLISTS_SHELL fires only after the
  // Following tab has been opened (or if the page initially routed to it).
  // Defaults to "skipped" so My Playlists landing doesn't trigger a useless
  // round-trip; once the user visits Following the latch sticks for the
  // session so tab switches stay cache-hot.
  const {
    followedPlaylistsJS,
    followedPlaylistsLoading,
    followedPlaylistsTotalCount,
    refetchFollowedPlaylists,
    loadMoreFollowedPlaylists,
    hasMoreFollowedPlaylists,
    isLoadingMoreFollowedPlaylists,
    handleRemoveFollowedPlaylist,
    handleReFollowPlaylist,
  } = useFollowedPlaylistsHook({
    uid,
    language,
    jwt,
    isLoggedIn,
    dispatch,
    staticText,
    queriesEnabled: queriesEnabled && followingEverActive,
    searchQuery: debouncedCollectionsSearch,
  })

  // ── Stabilize Apollo refetch / loadMore identities ─────────────────
  // Apollo does not guarantee a stable identity for `refetch` / `fetchMore`
  // wrappers — they rebuild on variable / load-state transitions. Listing
  // them directly in this hook's public-return memo dep array (below) made
  // the memo recompute on every Apollo broadcast even when no consumer-
  // visible state changed, which forced PlaylistTabContainer's React.memo
  // shallow compare to fail and cascaded into per-row re-renders. The
  // pattern below — useLatest-backed ref + stable useCallback wrapper —
  // gives consumers a function whose `.current` always reads the latest
  // Apollo callback at call-time, but whose identity never flips.
  // The same pattern is documented in useProfilePlaylists.ts (~L522)
  // where it's used to stabilize the focus/playlist-updated listener
  // effects.
  const refetchPlaylistsRef = useLatest(refetchPlaylists)
  const refetchFollowedPlaylistsRef = useLatest(refetchFollowedPlaylists)
  const loadMorePlaylistsRef = useLatest(loadMorePlaylists)
  const loadMoreFollowedPlaylistsRef = useLatest(loadMoreFollowedPlaylists)
  const stableRefetchPlaylists = useCallback(
    () => refetchPlaylistsRef.current(),
    [refetchPlaylistsRef],
  )
  const stableRefetchFollowedPlaylists = useCallback(
    () => refetchFollowedPlaylistsRef.current(),
    [refetchFollowedPlaylistsRef],
  )
  const stableLoadMorePlaylists = useCallback(
    () => loadMorePlaylistsRef.current(),
    [loadMorePlaylistsRef],
  )
  const stableLoadMoreFollowedPlaylists = useCallback(
    () => loadMoreFollowedPlaylistsRef.current(),
    [loadMoreFollowedPlaylistsRef],
  )

  // ── In Progress (Currently Watching) ──────────────────────────────
  // language + jwt feed the hook's internal useTitleGroupHydration call so
  // the difficulty pill renders on Currently Watching tiles — PR 2
  // removed `featureMedia.mediaAssets.aiMetadata.learnerLevel` from the
  // upstream GET_PROFILE_IN_PROGRESS_CAROUSEL selection; hydration
  // restores it via cache-and-network GET_CONTENT_FOR_TILE_HYDRATION.
  //
  // `inProgressIsHydrating` is the OR of two distinct loading windows:
  //   (a) the upstream carousel query owned by the caller (`inProgressLoading`)
  //   (b) `useInProgressEnrichment`'s internal title-group hydration
  // Single boolean is threaded down to `ProfileMyPlaylists` so the
  // Currently Watching row can always mount and skeleton-paint until BOTH
  // windows resolve — otherwise the row pops in once the carousel data
  // lands (the bug Phase 6 made visible by decoupling the carousel from
  // the page's blocking load).
  const {
    items: inProgressItems,
    isHydrating: inProgressInternalHydrating,
    overallProgress: inProgressOverallProgress,
  } = useInProgressEnrichment(inProgress, language, jwt)
  const inProgressIsHydrating = inProgressLoading || inProgressInternalHydrating

  // ── Meta tags (for hours watched stat) ─────────────────────────────
  // cache-and-network so a return visit paints the cached hours-watched
  // stat instantly while the background refetch reconciles.
  const { data: metaTagsData } = useQuery<GetUserMetaTagsResponse>(GET_USER_META_TAGS, {
    skip: !isLoggedIn || !queriesEnabled,
    fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
    errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
    context: { jwt },
  })

  // ── Derived: hours watched ─────────────────────────────────────────
  // Ref-stable derivation — narrows the dep from `metaTagsData` (the
  // whole Apollo response wrapper, which is reminted on every cache
  // write touching GET_USER_META_TAGS) to the inner `userMetaTags`
  // array reference, which Apollo normalisation keeps stable across
  // no-op emits. The O(N) reduce is skipped when the inner ref is
  // unchanged. Same intent as the sig-string ref pattern in
  // `useInProgressEnrichment.ts:227-245` and `useWatchHistoryData.ts:156-163`;
  // simpler here because the output is a primitive (no object identity
  // to preserve — React's Object.is on the number/null result already
  // keeps the surrounding slice memos quiet).
  const userMetaTags = _get(metaTagsData, 'userMetaTagList.userMetaTags', null) as
    Array<{ viewSeconds?: number }> | null
  const hoursWatchedRef = useRef<{ inputRef: unknown; value: number | null }>({
    inputRef: null,
    value: null,
  })
  if (userMetaTags !== hoursWatchedRef.current.inputRef) {
    const tags = userMetaTags || []
    const totalSeconds = tags.reduce(
      (sum, tag) => sum + (tag.viewSeconds || 0),
      0,
    )
    hoursWatchedRef.current = {
      inputRef: userMetaTags,
      value: totalSeconds > 0 ? Math.round(totalSeconds / 3600) : null,
    }
  }
  const hoursWatched = hoursWatchedRef.current.value

  // ── Derived: shared-on-profile count ───────────────────────────────
  // "Sharing N" reflects ONLY custom (user-authored) playlists that the user
  // has explicitly shared (PUBLIC/ANONYMOUS) via the share button on the
  // My Playlists tab. Three categories are intentionally excluded:
  //   - SYSTEM playlists ('default' = Watch Later, 'favorites' = Favorites).
  //     The share affordance for the user's own playlists lives elsewhere,
  //     and counting these here conflates "playlists I made and shared"
  //     with "system rows that happen to be visible".
  //   - Profile-level activity flags (Currently Watching, Watch History) —
  //     these are FieldVisibility flags on the user profile, not playlists.
  //   - Empty playlists — a public-but-empty playlist exposes nothing.
  // The early return on `vis === 'private'` is defensive: when the overall
  // profile is private, nothing is exposed regardless of per-playlist flags.
  const sharedOnProfileCount = useMemo(() => {
    // Was: `if (!playlistsRawData) return undefined` — gated on Apollo's
    // response wrapper. Swapped to `playlistsLoading` so this memo no longer
    // depends on the unstable wrapper reference (which recomputes on every
    // Apollo cache broadcast, defeating PlaylistTabContainer's shallow-prop
    // memo). `allRawPlaylists` here is the inner playlists array (defaulting
    // to EMPTY_PLAYLISTS while the query is in-flight); `playlistsLoading`
    // is the slice's "no data yet" signal — under cache-and-network,
    // useProfilePlaylists defines it as `shellLoading && !playlistsRawData`,
    // so on a revisit (cache hit) it's false immediately and this memo
    // can paint the cached count without waiting for the background refetch.
    if (playlistsLoading) return undefined
    const vis = (profileVisibility || 'public').toLowerCase()
    if (vis === 'private') return 0
    return allRawPlaylists.filter((pl: Playlist) => pl
      && !SYSTEM_PLAYLIST_TYPES.includes(pl.playlistType || '')
      && ['PUBLIC', 'ANONYMOUS'].includes((pl.visibility || '').toUpperCase())
      && (pl.itemCount || 0) > 0).length
  }, [allRawPlaylists, playlistsLoading, profileVisibility])

  // ── Derived: My Playlists tab count ────────────────────────────────
  // Count every row that <ProfileMyPlaylists> actually renders so the tab
  // badge stays in sync with what the user sees.  Each line below maps to
  // a single visible row whose presence is data-driven (no magic offsets):
  //   - Custom playlists: one row each — derived from `allUserPlaylistsJS`
  //   - Watch Later: one row when the backend returns the default playlist
  //   - Favorites: one row when the backend returns the favorites playlist
  //   - Currently Watching: one row only when in-progress items exist
  //     (the row is conditionally rendered in ProfileMyPlaylists)
  //   - Watch History: one row in owner view (`!isPreview`)
  // Removing items, deleting a playlist, or hiding the last in-progress
  // tile reactively updates this count via the dep array.
  const myPlaylistsCount = useMemo(() => {
    const customCount = allUserPlaylistsJS.length
    const watchLaterRow = defaultPlaylist ? 1 : 0
    const favoritesRow = favoritesPlaylist ? 1 : 0
    const currentlyWatchingRow = (inProgressItems?.length || 0) > 0 ? 1 : 0
    const watchHistoryRow = isPreview ? 0 : 1
    return customCount + watchLaterRow + favoritesRow + currentlyWatchingRow + watchHistoryRow
  }, [
    allUserPlaylistsJS.length,
    defaultPlaylist,
    favoritesPlaylist,
    inProgressItems,
    isPreview,
  ])

  // ── Derived: allowed visibilities ──────────────────────────────────
  const allowedVisibilities = useMemo(
    () => getAllowedPlaylistVisibilities(profileVisibility),
    [profileVisibility],
  )

  // ── Labels from static text ────────────────────────────────────────
  const seeMoreLabel = (staticText.getIn(['data', 'seeMore']) as string) || 'See more'
  const showLessLabel = (staticText.getIn(['data', 'showLess']) as string) || 'Show less'

  // Tab & search state are declared near the top of this hook (the followed-
  // playlists hook depends on the active tab).

  const handleCollectionsSearch = useCallback((value: string) => {
    setCollectionsSearch(value)
  }, [])

  // ── Derived: search result IDs ─────────────────────────────────────
  const searchResultIds = useMemo((): Set<string> | null => {
    if (!collectionsSearch) return null
    const lower = collectionsSearch.toLowerCase()
    const ids = allUserPlaylistsJS
      .filter((pl: Playlist) => (pl.playlistName || '').toLowerCase().includes(lower))
      .map((pl: Playlist) => pl.id)
    return new Set<string>(ids as string[])
  }, [collectionsSearch, allUserPlaylistsJS])

  // Split the return into five domain slices plus a top-level shared
  // layer. Each slice's dep list is narrow enough that
  // an Apollo broadcast touching one domain (e.g. watch-history) leaves
  // the other four slice references identity-stable. Consumers that
  // destructure a single slice get a `React.memo` shallow-compare win on
  // broadcasts that don't touch their slice — what the monolithic
  // 57-dep return memo defeated.
  //
  // Authoring rule: every binding in a slice's literal MUST appear in
  // that slice's dep list (and ONLY that slice's). The top-level memo's
  // deps are the slice references + the truly-shared scalars/labels.

  const myPlaylists = useMemo(() => ({
    allUserPlaylistsJS,
    // Inner array reference for consumers that need the raw shells
    // (share modal). Apollo preserves this identity on cache writes that
    // don't change the array content, so it's safe to expose here.
    allRawPlaylists,
    playlistsLoading,
    myPlaylistsCount,
    sharedOnProfileCount,
    allowedVisibilities,
    onUpdatePlaylistVisibility: handleUpdatePlaylistVisibility,
    onPlaylistAction: handlePlaylistAction,
    onRemovePlaylistItem: handleRemovePlaylistItem,
    onReorderPlaylistItem: handleReorderPlaylistItem,
    refetchPlaylists: stableRefetchPlaylists,
    loadMorePlaylists: stableLoadMorePlaylists,
    isLoadingMorePlaylists,
  }), [
    allUserPlaylistsJS,
    allRawPlaylists,
    playlistsLoading,
    myPlaylistsCount,
    sharedOnProfileCount,
    allowedVisibilities,
    handleUpdatePlaylistVisibility,
    handlePlaylistAction,
    handleRemovePlaylistItem,
    handleReorderPlaylistItem,
    stableRefetchPlaylists,
    stableLoadMorePlaylists,
    isLoadingMorePlaylists,
  ])

  const followed = useMemo(() => ({
    followedPlaylistsJS,
    followedPlaylistsTotalCount,
    followedPlaylistsLoading,
    onRemoveFollowedPlaylist: handleRemoveFollowedPlaylist,
    onReFollowPlaylist: handleReFollowPlaylist,
    refetchFollowedPlaylists: stableRefetchFollowedPlaylists,
    loadMoreFollowedPlaylists: stableLoadMoreFollowedPlaylists,
    hasMoreFollowedPlaylists,
    isLoadingMoreFollowedPlaylists,
  }), [
    followedPlaylistsJS,
    followedPlaylistsTotalCount,
    followedPlaylistsLoading,
    handleRemoveFollowedPlaylist,
    handleReFollowPlaylist,
    stableRefetchFollowedPlaylists,
    stableLoadMoreFollowedPlaylists,
    hasMoreFollowedPlaylists,
    isLoadingMoreFollowedPlaylists,
  ])

  const watchHistory = useMemo(() => ({
    inProgressItems,
    inProgressIsHydrating,
    // Time-weighted "{percent}% overall" stat for the Currently Watching
    // row header. Computed inside useInProgressEnrichment from the raw
    // (pre-dedup) in-progress positions so series-duplicate episodes
    // each contribute to the total — see that hook's docblock.
    inProgressOverallProgress,
    watchHistoryItems,
    watchHistoryTotalCount,
    watchHistoryTotalDuration,
    watchHistoryLoading,
    onRemoveWatchHistoryItem,
    onHideInProgressItem: handleHideInProgressItem,
  }), [
    inProgressItems,
    inProgressIsHydrating,
    inProgressOverallProgress,
    watchHistoryItems,
    watchHistoryTotalCount,
    watchHistoryTotalDuration,
    watchHistoryLoading,
    onRemoveWatchHistoryItem,
    handleHideInProgressItem,
  ])

  const items = useMemo(() => ({
    defaultPlaylistId: defaultPlaylist ? defaultPlaylist.id : null,
    // Full Watch Later (default) playlist object — needed for the share
    // button + sharing dropdown wiring on /playlist/default.
    watchLaterPlaylist: defaultPlaylist,
    // Full Favorites playlist object — same purpose as watchLaterPlaylist.
    favoritesPlaylist,
    onRemoveWatchLaterItem: handleRemoveWatchLaterItem,
    onReorderWatchLaterItem: handleReorderWatchLaterItem,
    onRemoveFavoritesItem: handleRemoveFavoritesItem,
    onReorderFavoritesItem: handleReorderFavoritesItem,
  }), [
    defaultPlaylist,
    favoritesPlaylist,
    handleRemoveWatchLaterItem,
    handleReorderWatchLaterItem,
    handleRemoveFavoritesItem,
    handleReorderFavoritesItem,
  ])

  const search = useMemo(() => ({
    collectionsSearch,
    onCollectionsSearch: handleCollectionsSearch,
    searchResultIds,
  }), [collectionsSearch, handleCollectionsSearch, searchResultIds])

  // Top-level return — the five slice references plus shared scalars
  // (collectionsTab, labels, profile flags, language). Threading these as
  // top-level keys lets the tab bar / search bar / private banner read
  // them without crossing a slice boundary. `setCollectionsTab` is a
  // stable React setter so it's omitted from the dep list.
  return useMemo(() => ({
    myPlaylists,
    followed,
    watchHistory,
    items,
    search,
    collectionsTab,
    onCollectionsTabChange: setCollectionsTab,
    isPreview,
    isProfilePrivate: (profileVisibility || 'public').toLowerCase() === 'private',
    language,
    hoursWatched,
    authorUsername,
    authorUid,
    seeMoreLabel,
    showLessLabel,
  }), [
    myPlaylists,
    followed,
    watchHistory,
    items,
    search,
    collectionsTab,
    isPreview,
    profileVisibility,
    language,
    hoursWatched,
    authorUsername,
    authorUid,
    seeMoreLabel,
    showLessLabel,
  ])
}
